/**
 * Biometric login lifecycle — bound to the AuthContext.
 *
 * • After a successful password/PIN login on a native device, prompt the
 *   user (once) to enable biometric unlock.
 * • On subsequent app launches, if biometric login is enabled and a token
 *   is present, require the biometric prompt before restoring the session.
 * • Token itself lives in Capacitor SecureStorage (Keychain on iOS,
 *   EncryptedSharedPreferences on Android) — plain localStorage on web.
 *
 * The helper degrades to a no-op on web so the same auth code paths run
 * everywhere.
 */
import { toast } from "sonner";
import {
  biometryAvailable,
  biometryLabel,
  biometryAuthenticate,
  secureSet,
  secureGet,
  secureDelete,
  isNative,
} from "./native";

const KEY_TOKEN = "icaard_auth_token";
const KEY_ENABLED = "icaard_biometric_enabled";
const KEY_PROMPTED = "icaard_biometric_prompted";

export async function persistToken(token) {
  if (!token) return;
  if (isNative()) await secureSet(KEY_TOKEN, token);
  else try { localStorage.setItem("icard_token", token); } catch (_) {}
}
export async function readPersistedToken() {
  if (isNative()) return await secureGet(KEY_TOKEN);
  try { return localStorage.getItem("icard_token"); } catch (_) { return null; }
}
export async function clearPersistedToken() {
  if (isNative()) await secureDelete(KEY_TOKEN);
  try { localStorage.removeItem("icard_token"); } catch (_) {}
}

export async function isBiometricEnabled() {
  if (!isNative()) return false;
  const v = await secureGet(KEY_ENABLED);
  return v === "1";
}
export async function setBiometricEnabled(enabled) {
  if (!isNative()) return;
  if (enabled) await secureSet(KEY_ENABLED, "1");
  else await secureDelete(KEY_ENABLED);
}

/**
 * Prompt the user (only once) to enable biometric login after their first
 * successful password/PIN authentication. Called by AuthContext.login().
 */
export async function maybeOfferBiometricSetup() {
  if (!isNative()) return;
  const available = await biometryAvailable();
  if (!available) return;
  const already = await secureGet(KEY_PROMPTED);
  if (already === "1") return;
  const enabled = await isBiometricEnabled();
  if (enabled) return;
  await secureSet(KEY_PROMPTED, "1");
  const label = await biometryLabel();
  // Ask once, non-blocking — the user can also flip it from Profile → Security.
  if (window.confirm(`Enable ${label} for faster sign-in?`)) {
    const ok = await biometryAuthenticate(`Enable ${label} for iCaard`);
    if (ok) {
      await setBiometricEnabled(true);
      toast.success(`${label} sign-in enabled`);
    } else {
      toast.info(`${label} not enabled — you can turn it on later in Profile → Security.`);
    }
  }
}

/**
 * Called on app launch. If biometric is enabled and a token is stored,
 * gate access to the app behind a biometric prompt.
 * Returns true if the session may proceed, false if the user should be
 * pushed back to the login screen.
 */
export async function unlockWithBiometricIfNeeded() {
  if (!isNative()) return true;
  const enabled = await isBiometricEnabled();
  if (!enabled) return true;
  const token = await secureGet(KEY_TOKEN);
  if (!token) return true;
  const label = await biometryLabel();
  const ok = await biometryAuthenticate(`Unlock iCaard with ${label}`);
  return ok;
}
